import React, { useState, useEffect, useCallback, useMemo } from "react";
import API_BASE_URL from "../RestAPI";
import { showErrorToast } from "../../constants/ToastService.js";
import "../SharedTable.css";
import FilterDropdown from "../Common Components/FilterDropdown";

const PAGE_SIZE = 15;

// Default view = Sprint Tickets (Story + Epic + entries with no linked ticket
// such as Regression Testing). Bugs can be toggled on as a separate group.
const TICKET_SCOPE = {
    sprint: { key: 'sprint', label: 'Sprint Tickets', match: (r) => r.ticket_type !== 'Bug' },
    bugs:   { key: 'bugs',   label: 'Bugs',           match: (r) => r.ticket_type === 'Bug' },
    all:    { key: 'all',    label: 'All',            match: ()  => true },
};

const QATimeTrackingReport = () => {
    const [reportData, setReportData] = useState([]);
    const [qaMembers, setQaMembers] = useState([]);
    const [selectedQA, setSelectedQA] = useState('');
    const [fromDate, setFromDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().split('T')[0];
    });
    const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [summary, setSummary] = useState({ total_entries: 0, total_hours: 0 });
    const [search, setSearch] = useState('');
    const [scope, setScope] = useState('sprint');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`
    };

    const fetchReport = useCallback(async () => {
        if (!fromDate || !toDate) return;
        setLoading(true);
        try {
            let url = `${API_BASE_URL}reports/qa/time-tracking/?from_date=${fromDate}&to_date=${toDate}`;
            if (selectedQA) url += `&qa_member_id=${selectedQA}`;
            const res = await fetch(url, { method: 'GET', headers });
            const data = await res.json();
            if (data.status === 'success') {
                setReportData(data.data || []);
                setQaMembers(data.qa_members || []);
                setSummary(data.summary || { total_entries: 0, total_hours: 0 });
            } else {
                showErrorToast(data.message);
            }
        } catch (err) {
            console.error(err);
            showErrorToast("Failed to fetch report");
        }
        setLoading(false);
    }, [fromDate, toDate, selectedQA]);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    const scopedData = useMemo(() => {
        const matcher = TICKET_SCOPE[scope]?.match || TICKET_SCOPE.sprint.match;
        return reportData.filter(matcher);
    }, [reportData, scope]);

    const counts = useMemo(() => ({
        sprint: reportData.filter(TICKET_SCOPE.sprint.match).length,
        bugs:   reportData.filter(TICKET_SCOPE.bugs.match).length,
        all:    reportData.length,
    }), [reportData]);

    const filtered = useMemo(() => {
        if (!search.trim()) return scopedData;
        const q = search.toLowerCase();
        return scopedData.filter(r =>
            (r.qa_name || '').toLowerCase().includes(q) ||
            (r.qa_member_name || '').toLowerCase().includes(q) ||
            (r.ticket_id || '').toLowerCase().includes(q) ||
            (r.ticket_description || '').toLowerCase().includes(q) ||
            (r.sprint_name || '').toLowerCase().includes(q) ||
            (r.testing_type || '').toLowerCase().includes(q)
        );
    }, [scopedData, search]);

    const paginated = useMemo(() => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [filtered, page]);
    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

    useEffect(() => { setPage(1); }, [scope]);

    // If the current page falls outside the new page range (data shrank after
    // a filter/date change), snap back into range instead of showing an empty page.
    useEffect(() => {
        if (totalPages > 0 && page > totalPages) setPage(totalPages);
    }, [totalPages, page]);

    const formatDateTime = (dt) => {
        if (!dt) return '-';
        const d = new Date(dt);
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        let hh = d.getHours();
        const min = String(d.getMinutes()).padStart(2, '0');
        const ampm = hh >= 12 ? 'PM' : 'AM';
        hh = hh % 12 || 12;
        return `${dd}-${mm}-${yyyy} ${hh}:${min} ${ampm}`;
    };

    const getStatusPill = (status, isAutoLogout) => {
        if (isAutoLogout) return <span className="fd-status-pill fd-status-pill--danger">Auto Logged Out</span>;
        switch (status) {
            case 'In Progress': return <span className="fd-status-pill fd-status-pill--info">In Progress</span>;
            case 'Completed': return <span className="fd-status-pill fd-status-pill--success">Completed</span>;
            case 'Auto Logged Out': return <span className="fd-status-pill fd-status-pill--danger">Auto Logged Out</span>;
            case 'Paused': return <span className="fd-status-pill fd-status-pill--muted">Paused</span>;
            default: return <span className="fd-status-pill">{status}</span>;
        }
    };

    const getTypePill = (type) => {
        if (type === 'Regression Testing') return <span className="fd-status-pill fd-status-pill--muted">Regression</span>;
        return <span className="fd-status-pill fd-status-pill--info">Ticket</span>;
    };

    return (
        <div style={{ padding: '20px 28px' }}>
            <div className="fd-selector-row">
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>From</label>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                        style={{
                            padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)',
                            background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.8rem'
                        }}
                    />
                    <label style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>To</label>
                    <input
                        type="date"
                        value={toDate}
                        onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                        style={{
                            padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border)',
                            background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.8rem'
                        }}
                    />
                </div>
                <FilterDropdown
                    label="QA Member"
                    value={selectedQA}
                    options={qaMembers || []}
                    onChange={(val) => { setSelectedQA(val); setPage(1); }}
                    renderLabel={o => o?.name || ''}
                    getValue={o => String(o?.id)}
                />
            </div>

            <div className="fd-card">
                <div className="fd-card-header">
                    <div>
                        <div className="fd-card-title">QA Time Tracking Report</div>
                        <div className="fd-card-subtitle">{filtered.length} record(s) | Total Hours: {summary.total_hours}h</div>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', padding: '0 16px 12px', flexWrap: 'wrap' }}>
                    <div className="fd-summary-chip">
                        <span className="fd-summary-chip__value">{summary.total_entries}</span>
                        <span className="fd-summary-chip__label">Total Entries</span>
                    </div>
                    <div className="fd-summary-chip">
                        <span className="fd-summary-chip__value">{summary.total_hours}h</span>
                        <span className="fd-summary-chip__label">Total Hours</span>
                    </div>
                </div>

                <div className="fd-toolbar">
                    <div className="fd-scope-pills">
                        {['sprint', 'bugs', 'all'].map(k => (
                            <button
                                key={k}
                                className={`fd-scope-pill ${scope === k ? 'fd-scope-pill--active' : ''}`}
                                onClick={() => setScope(k)}
                            >
                                {TICKET_SCOPE[k].label} <span className="fd-scope-pill__count">{counts[k]}</span>
                            </button>
                        ))}
                    </div>
                    <input
                        className="fd-search"
                        placeholder="Search by QA name, ticket, sprint, description..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    />
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table className="fd-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>QA Name</th>
                                <th>Ticket Type</th>
                                <th>Testing Type</th>
                                <th>Sprint</th>
                                <th>Ticket</th>
                                <th>Description</th>
                                <th>Start Time</th>
                                <th>End Time</th>
                                <th>Hours</th>
                                <th>Status</th>
                                <th>Remarks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={12} className="fd-empty">Loading...</td></tr>
                            ) : paginated.length > 0 ? paginated.map((row, i) => {
                                const tType = row.ticket_type;
                                return (
                                    <tr key={row.id}>
                                        <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                                        <td>{row.qa_member_name || row.qa_name || '-'}</td>
                                        <td>
                                            {tType ? (
                                                <span className={`fd-type-pill fd-type-pill--${tType.toLowerCase()}`}>{tType}</span>
                                            ) : '-'}
                                        </td>
                                        <td>{getTypePill(row.testing_type)}</td>
                                        <td>{row.sprint_name || '-'}</td>
                                        <td style={{ fontWeight: 600 }}>{row.ticket_id || '-'}</td>
                                        <td style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            title={row.ticket_description || ''}>
                                            {row.ticket_description || '-'}
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.start_time)}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.end_time)}</td>
                                        <td style={{ fontWeight: 600 }}>{row.total_hours != null ? `${row.total_hours}h` : '-'}</td>
                                        <td>{getStatusPill(row.status, row.is_auto_logged_out)}</td>
                                        <td style={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                            title={row.remarks || ''}>
                                            {row.remarks || '-'}
                                        </td>
                                    </tr>
                                );
                            }) : (
                                <tr><td colSpan={12} className="fd-empty">No data found for the selected filters.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="fd-pagination">
                        <span>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
                        <div className="fd-pagination-controls">
                            <button className="fd-page-btn" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .map((p, idx, arr) => (
                                    <React.Fragment key={p}>
                                        {idx > 0 && arr[idx - 1] !== p - 1 && <span className="fd-page-ellipsis">…</span>}
                                        <button className={`fd-page-btn ${p === page ? 'fd-page-btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                                    </React.Fragment>
                                ))}
                            <button className="fd-page-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QATimeTrackingReport;
